<script setup lang="ts">
import { Search, XMark } from "@/components/icons";
import { computed, onMounted, ref, watch } from "vue";
import { debounce } from "@/lib/utils";
import AllTab from "./AllTab.vue";
import EditionsTab from "./EditionsTab.vue";
import ArticlesTab from "./ArticlesTab.vue";
import { useI18n } from "vue-i18n";
import { AnalyticsEvent, useAnalytics } from "@/lib/analytics";
import {
  DialogClose,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "reka-ui";

const { t } = useI18n();
const { logEvent } = useAnalytics();

const type = ref<"all" | "magazines" | "articles">("all");
const searchInput = ref<HTMLInputElement>();
const inputQuery = ref("");
const searchQuery = ref("");

const options = [
  { label: t("search.label.all"), value: "all" },
  { label: t("search.label.magazines"), value: "magazines" },
  { label: t("search.label.articles"), value: "articles" },
];

watch(inputQuery, (newValue) => {
  debounceInput(newValue);
});

const debounceInput = debounce((query: string) => {
  searchQuery.value = query;
  logEvent(AnalyticsEvent.Search, { query });
}, 500);

const emptyDescription = computed(() => {
  if (type.value === "articles") {
    return t("search.start_articles");
  } else if (type.value === "magazines") {
    return t("search.start_magazines");
  } else {
    return t("search.start_all");
  }
});

onMounted(() => {
  setTimeout(() => {
    searchInput.value?.focus();
  }, 100);
});
</script>

<template>
  <TabsRoot
    v-model="type"
    class="rounded-xl overflow-hidden flex flex-col w-full"
  >
    <div>
      <div class="flex items-center p-4">
        <div class="relative mr-4 flex-1">
          <Search class="absolute left-4 top-3 text-icon-secondary" />
          <input
            class="py-3 pl-12 pr-4 rounded-xl bg-background-secondary border border-border-primary w-full h-12"
            type="text"
            :placeholder="$t('search.placeholder')"
            v-model="inputQuery"
            ref="searchInput"
          />
        </div>
        <DialogClose class="btn-round-secondary">
          <XMark />
        </DialogClose>
      </div>
      <TabsList class="flex relative border-b border-border-primary">
        <TabsIndicator
          class="absolute -bottom-px h-[2px] bg-border-brand w-(--reka-tabs-indicator-size) translate-x-(--reka-tabs-indicator-position) transition-all duration-300"
        />
        <TabsTrigger
          v-for="option in options"
          :value="option.value"
          class="px-4 py-2 relative z-1 h-10 transition-colors data-[state=active]:text-text-primary data-[state=inactive]:text-text-tertiary"
        >
          {{ option.label }}
        </TabsTrigger>
      </TabsList>
    </div>
    <div class="overflow-auto" id="search-modal-container">
      <div v-if="searchQuery.length < 1" class="min-h-40 flex-center">
        <div class="text-center">
          <p class="font-bold">{{ $t("search.start_title") }}</p>
          <p class="text-text-secondary">{{ emptyDescription }}</p>
        </div>
      </div>
      <div v-else>
        <TabsContent value="all">
          <AllTab :searchQuery="searchQuery" @openTab="(t) => (type = t)" />
        </TabsContent>
        <TabsContent value="magazines">
          <EditionsTab :searchQuery="searchQuery" />
        </TabsContent>
        <TabsContent value="articles">
          <ArticlesTab :searchQuery="searchQuery" />
        </TabsContent>
      </div>
    </div>
  </TabsRoot>
</template>
